import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Moon, Sun, FileText, User as UserIcon, Mail, Save, X } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { fetchSavedBills, toggleSaveBill, resolveBillId } from '../lib/savedBills';
import { fetchFavoritedBills, toggleFavoriteBill } from '../lib/favoritedBills';
import UserMenu from '../components/UserMenu';
import BillCard from '../components/BillCard';
import DevelopmentBanner from '../components/DevelopmentBanner';
import { buildApiUrl } from '../config';
import { formatDate, getDaysActive, getMostRecentActionDate } from '../scripts/bills/billUtils';
import { getPolicyTagColor } from '../scripts/bills/policyTagUtils';

export default function Account({ isDarkMode, setIsDarkMode }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [savingNewsletter, setSavingNewsletter] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [savedBills, setSavedBills] = useState([]);
  const [favoritedBills, setFavoritedBills] = useState([]);
  const [activeTab, setActiveTab] = useState('saved'); // 'saved' | 'favorited'
  const [savedSenatorId, setSavedSenatorId] = useState(null);
  const [savedRepId, setSavedRepId] = useState(null);
  const [savedSenatorPolitician, setSavedSenatorPolitician] = useState(null);
  const [savedRepPolitician, setSavedRepPolitician] = useState(null);
  const [clearingPolitician, setClearingPolitician] = useState(null);

  // Quick lookups for whether a bill is saved/favorited
  const savedIdSet = useMemo(() => new Set((savedBills || []).map((r) => r.bill_id)), [savedBills]);
  const favoritedIdSet = useMemo(() => new Set((favoritedBills || []).map((r) => r.bill_id)), [favoritedBills]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      const u = data?.session?.user || null;
      if (!u) {
        navigate('/signin?return=' + encodeURIComponent('/account'), { replace: true });
        return;
      }
      setUser(u);
      try {
        const { data: prof } = await supabase
          .from('profiles')
          .select('username, email, newsletter_opt_in, saved_senator_politician_id, saved_representative_politician_id')
          .eq('user_id', u.id)
          .maybeSingle();
        setUsername(prof?.username || u.user_metadata?.full_name || (u.email ? u.email.split('@')[0] : ''));
        setEmail(prof?.email || u.email || '');
        setNewsletterOptIn(prof?.newsletter_opt_in ?? false);
        setSavedSenatorId(prof?.saved_senator_politician_id ?? null);
        setSavedRepId(prof?.saved_representative_politician_id ?? null);
        const [saved, favorites] = await Promise.all([
          fetchSavedBills(),
          fetchFavoritedBills()
        ]);
        setSavedBills(saved);
        setFavoritedBills(favorites);
      } catch (e) {
        // noop
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // Resolve saved politician ids to full politician objects via API
  useEffect(() => {
    if (!savedSenatorId && !savedRepId) {
      setSavedSenatorPolitician(null);
      setSavedRepPolitician(null);
      return;
    }
    let cancelled = false;
    fetch(buildApiUrl('/api/politicians'))
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        const senator = savedSenatorId ? list.find((p) => String(p.id) === String(savedSenatorId)) : null;
        const rep = savedRepId ? list.find((p) => String(p.id) === String(savedRepId)) : null;
        setSavedSenatorPolitician(senator || null);
        setSavedRepPolitician(rep || null);
      })
      .catch(() => {
        if (!cancelled) {
          setSavedSenatorPolitician(null);
          setSavedRepPolitician(null);
        }
      });
    return () => { cancelled = true; };
  }, [savedSenatorId, savedRepId]);

  const handleClearSavedPolitician = async (chamber) => {
    if (!user) return;
    const col = chamber === 'Senate' ? 'saved_senator_politician_id' : 'saved_representative_politician_id';
    setClearingPolitician(chamber);
    setError('');
    setStatus('');
    try {
      const { data: existing } = await supabase.from('profiles').select('*').eq('user_id', user.id).maybeSingle();
      const updates = { ...(existing || {}), user_id: user.id, [col]: null };
      const { error: upErr } = await supabase.from('profiles').upsert(updates, { onConflict: 'user_id' });
      if (upErr) throw upErr;
      if (chamber === 'Senate') {
        setSavedSenatorId(null);
        setSavedSenatorPolitician(null);
      } else {
        setSavedRepId(null);
        setSavedRepPolitician(null);
      }
      setStatus(chamber === 'Senate' ? 'Saved senator cleared.' : 'Saved representative cleared.');
      setTimeout(() => setStatus(''), 3000);
    } catch (e) {
      setError(e?.message || 'Failed to clear.');
    } finally {
      setClearingPolitician(null);
    }
  };

  const handleSaveUsername = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    setStatus('');
    try {
      // Update app profile
      const { error: upErr } = await supabase
        .from('profiles')
        .upsert({ user_id: user.id, username, email, newsletter_opt_in: newsletterOptIn }, { onConflict: 'user_id' });
      if (upErr) throw upErr;

      // Sync to Supabase Auth user metadata so auth.users reflects it
      const { error: authErr } = await supabase.auth.updateUser({
        data: { full_name: username, username }
      });
      if (authErr) throw authErr;

      // Refresh local user state
      const { data: sessionData } = await supabase.auth.getSession();
      setUser(sessionData?.session?.user || user);

      setStatus('Username saved successfully.');
    } catch (e) {
      setError(e?.message || 'Failed to save username.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!user) return;
    const next = prompt('Enter new email:', email);
    if (!next || next === email) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(next)) {
      alert('Please enter a valid email.');
      return;
    }
    if (!confirm(`Are you sure you want to change your email to ${next}? You may need to verify via email.`)) {
      return;
    }
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const { error: updErr } = await supabase.auth.updateUser({ email: next });
      if (updErr) throw updErr;
      // Reflect in profile table as well
      await supabase.from('profiles').upsert({ user_id: user.id, username, email: next }, { onConflict: 'user_id' });
      setEmail(next);
      setStatus('Email update requested. Check your inbox to confirm.');
    } catch (e) {
      setError(e?.message || 'Failed to update email.');
    } finally {
      setSaving(false);
    }
  };

  const handleNewsletterChange = async (e) => {
    if (!user) return;
    const next = e.target.checked;
    setNewsletterOptIn(next);
    setError('');
    setStatus('');
    setSavingNewsletter(true);
    try {
      const { error: upErr } = await supabase
        .from('profiles')
        .upsert(
          { user_id: user.id, username, email, newsletter_opt_in: next },
          { onConflict: 'user_id' }
        );
      if (upErr) throw upErr;
      setStatus('Newsletter preference saved.');
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      setNewsletterOptIn(!next); // revert on failure
      setError(err?.message || 'Failed to save newsletter preference.');
    } finally {
      setSavingNewsletter(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!email) {
      alert('No email on file.');
      return;
    }
    if (!confirm(`Send a password reset email to ${email}?`)) return;
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: rpErr } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (rpErr) throw rpErr;
      setStatus('Password reset email sent.');
    } catch (e) {
      setError(e?.message || 'Failed to send reset email.');
    } finally {
      setSaving(false);
    }
  };

  function formatDistrictLabel(district, fallback) {
    if (!district) return fallback;
    const s = String(district).trim();
    return /^district\s+/i.test(s) ? s : 'District ' + s;
  }

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/about" className="inline-flex items-center">
                <span className="font-bold text-3xl md:text-4xl select-none" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <span className="text-[#3b82f6]">Civic</span>
                  <span className="text-[#ef4444]">Duty</span>
                </span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-all duration-200 ${isDarkMode ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => navigate('/bills')}
                className={`flex items-center px-4 py-2 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } rounded-lg transition-all shadow-sm hover:shadow-md transform hover:scale-105`}
              >
                <FileText className="w-4 h-4 mr-2" />
                View Bills
              </button>
              <UserMenu isDarkMode={isDarkMode} />
            </div>
          </div>
          {/* Mobile simplified header */}
          <div className="md:hidden flex items-center justify-between">
            <Link to="/about" className="inline-flex items-center">
              <span className="font-bold text-2xl select-none" style={{ fontFamily: 'Inter, sans-serif' }}>
                <span className="text-[#3b82f6]">Civic</span>
                <span className="text-[#ef4444]">Duty</span>
              </span>
            </Link>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-all duration-200 ${isDarkMode ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <UserMenu isDarkMode={isDarkMode} />
            </div>
          </div>
        </div>
      </header>

      <DevelopmentBanner isDarkMode={isDarkMode} />

      <section className="py-6 md:py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Heading */}
          <div className="mb-6">
            <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>My Account</h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Manage your profile information.</p>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left column: account settings + representatives stacked */}
            <div className="space-y-6">
            {/* Account settings card */}
            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-2xl shadow-sm p-6 md:p-8`}>
              {/* Username */}
              <div className="mb-5">
                <label className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'} text-sm font-semibold mb-2 block`}>Username</label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <UserIcon className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className={`w-full pl-9 pr-3 py-2 rounded-lg border focus:outline-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'}`}
                      placeholder="Your username"
                    />
                  </div>
                  <button
                    onClick={handleSaveUsername}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </button>
                </div>
              </div>

              {/* Email */}
              <div className="mb-6">
                <label className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'} text-sm font-semibold mb-2 block`}>Email</label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Mail className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                      type="email"
                      value={email}
                      readOnly
                      className={`w-full pl-9 pr-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                    />
                  </div>
                  <button
                    onClick={handleChangeEmail}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-100"
                  >
                    Change
                  </button>
                </div>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs mt-1`}>Changing email will send a confirmation link.</p>
              </div>

              {/* Newsletter opt-in (auto-saves on toggle; opt-out by unchecking) */}
              <div className="mb-6">
                <label className={`flex items-center gap-3 cursor-pointer ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  <input
                    type="checkbox"
                    checked={newsletterOptIn}
                    onChange={handleNewsletterChange}
                    disabled={savingNewsletter}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <span className="text-sm font-medium">Subscribe to weekly development newsletter</span>
                </label>
                <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'} text-xs mt-1 ml-7`}>Saves automatically. Uncheck to opt out.</p>
              </div>

              {status && <div className="text-green-500 text-sm mb-2">{status}</div>}
              {error && <div className="text-red-500 text-sm mb-2">{error}</div>}

              {/* Reset password */}
              <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-xl p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold`}>Reset Password</h3>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Send a reset link to your email to create a new password.</p>
                  </div>
                  <button
                    onClick={handleSendResetEmail}
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    Send Reset Email
                  </button>
                </div>
              </div>
            </div>

            {/* My representatives card block */}
            <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-2xl shadow-sm p-6 md:p-8`}>
              <h3 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold mb-1`}>My representatives</h3>
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-xs mb-4`}>To change, go to the homepage, enter your district, and save a senator or representative.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* State senator slot */}
                <div className={`flex items-center gap-3 p-3 rounded-lg border min-h-[72px] ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  {savedSenatorPolitician ? (
                    <>
                      <Link
                        to={`/politicians/${savedSenatorPolitician.id}`}
                        className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                      >
                        <img
                          src={savedSenatorPolitician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(savedSenatorPolitician.name)}&background=random&color=fff`}
                          alt={savedSenatorPolitician.name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {savedSenatorPolitician.name}
                          </div>
                          <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {formatDistrictLabel(savedSenatorPolitician.district, 'State Senator')}
                          </div>
                          <span className={`inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${savedSenatorPolitician.party === 'Democrat' ? (isDarkMode ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-800') : savedSenatorPolitician.party === 'Republican' ? (isDarkMode ? 'bg-red-900/50 text-red-200' : 'bg-red-100 text-red-800') : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')}`}>
                            {savedSenatorPolitician.party || '—'}
                          </span>
                        </div>
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); handleClearSavedPolitician('Senate'); }}
                        disabled={clearingPolitician === 'Senate'}
                        className={`p-1.5 rounded flex-shrink-0 ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'}`}
                        title="Remove saved senator"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/"
                      className={`flex items-center gap-3 flex-1 text-sm ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'} transition-colors`}
                    >
                      <div className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <UserIcon className={`w-6 h-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <span>No state senator saved</span>
                        <span className="block text-xs mt-0.5">Go to homepage to set</span>
                      </div>
                    </Link>
                  )}
                </div>
                {/* State representative slot */}
                <div className={`flex items-center gap-3 p-3 rounded-lg border min-h-[72px] ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  {savedRepPolitician ? (
                    <>
                      <Link
                        to={`/politicians/${savedRepPolitician.id}`}
                        className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                      >
                        <img
                          src={savedRepPolitician.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(savedRepPolitician.name)}&background=random&color=fff`}
                          alt={savedRepPolitician.name}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {savedRepPolitician.name}
                          </div>
                          <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {formatDistrictLabel(savedRepPolitician.district, 'State Representative')}
                          </div>
                          <span className={`inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${savedRepPolitician.party === 'Democrat' ? (isDarkMode ? 'bg-blue-900/50 text-blue-200' : 'bg-blue-100 text-blue-800') : savedRepPolitician.party === 'Republican' ? (isDarkMode ? 'bg-red-900/50 text-red-200' : 'bg-red-100 text-red-800') : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700')}`}>
                            {savedRepPolitician.party || '—'}
                          </span>
                        </div>
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); handleClearSavedPolitician('House'); }}
                        disabled={clearingPolitician === 'House'}
                        className={`p-1.5 rounded flex-shrink-0 ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'}`}
                        title="Remove saved representative"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/"
                      className={`flex items-center gap-3 flex-1 text-sm ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'} transition-colors`}
                    >
                      <div className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <UserIcon className={`w-6 h-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <span>No state representative saved</span>
                        <span className="block text-xs mt-0.5">Go to homepage to set</span>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            </div>
            </div>

            {/* Right: saved bills - render as real BillCards (no extra card chrome) */}
            <div className="p-0">
              <div className="flex items-center gap-2 mb-4">
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${activeTab === 'saved'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : isDarkMode ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'}`}
                  onClick={() => setActiveTab('saved')}
                >
                  Saved Bills
                </button>
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${activeTab === 'favorited'
                    ? 'bg-yellow-500 text-white border-yellow-500'
                    : isDarkMode ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'}`}
                  onClick={() => setActiveTab('favorited')}
                >
                  Favorited Bills
                </button>
              </div>
              {activeTab === 'saved' ? (
                <>
                  <h3 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold mb-4`}>Saved Bills</h3>
                  {savedBills.length === 0 ? (
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>You have no saved bills yet.</p>
                  ) : (
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                      {savedBills.map((row) => {
                        const b = row?.bills || {};
                        // Helper to get latest action date excluding "Effective Date" (frontend-only fix)
                        const getMostRecentActionDateExcludingEffective = (bill) => {
                          if (!bill.history || bill.history.length === 0) {
                            return getMostRecentActionDate(bill);
                          }
                          // Find first action that is NOT "Effective Date"
                          const nonEffectiveAction = bill.history.find(action => {
                            const actionLower = (action.action || '').toLowerCase();
                            const code = action.code || '';
                            return code !== 'EFF' && !actionLower.includes('effective date');
                          });
                          return nonEffectiveAction?.date || getMostRecentActionDate(bill);
                        };
                        const enriched = {
                          ...b,
                          mostRecentDate: getMostRecentActionDateExcludingEffective(b),
                        };
                        return (
                          <BillCard
                            key={row.bill_id}
                            bill={enriched}
                            isDarkMode={isDarkMode}
                            onBillClick={(bill) => navigate(`/bills/${bill.bill_number}`, { state: { returnUrl: '/account' } })}
                            formatDate={formatDate}
                            getPolicyTagColor={(tag) => getPolicyTagColor(tag, isDarkMode)}
                            getDaysActive={getDaysActive}
                            isSaved={savedIdSet.has(row.bill_id)}
                            isFavorited={favoritedIdSet.has(row.bill_id)}
                            onToggleSave={async (bill) => {
                              try {
                                const id = bill.id || (await resolveBillId(bill));
                                if (!id) return;
                                await toggleSaveBill(id, false);
                                setSavedBills((prev) => prev.filter((r) => r.bill_id !== id));
                              } catch (e) {
                                alert('Failed to update saved state.');
                              }
                            }}
                            onToggleFavorite={async (bill) => {
                              try {
                                const id = bill.id || (await resolveBillId(bill));
                                if (!id) return;
                                const shouldFavorite = !favoritedIdSet.has(id);
                                await toggleFavoriteBill(id, shouldFavorite);
                                setFavoritedBills((prev) =>
                                  shouldFavorite
                                    ? [{ bill_id: id, bills: bill }, ...prev.filter((r) => r.bill_id !== id)]
                                    : prev.filter((r) => r.bill_id !== id)
                                );
                              } catch (e) {
                                alert('Failed to update favorite state.');
                              }
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h3 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-semibold mb-4`}>Favorited Bills</h3>
                  {favoritedBills.length === 0 ? (
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>You have no favorited bills yet.</p>
                  ) : (
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                      {favoritedBills.map((row) => {
                        const b = row?.bills || {};
                        // Helper to get latest action date excluding "Effective Date" (frontend-only fix)
                        const getMostRecentActionDateExcludingEffective = (bill) => {
                          if (!bill.history || bill.history.length === 0) {
                            return getMostRecentActionDate(bill);
                          }
                          // Find first action that is NOT "Effective Date"
                          const nonEffectiveAction = bill.history.find(action => {
                            const actionLower = (action.action || '').toLowerCase();
                            const code = action.code || '';
                            return code !== 'EFF' && !actionLower.includes('effective date');
                          });
                          return nonEffectiveAction?.date || getMostRecentActionDate(bill);
                        };
                        const enriched = {
                          ...b,
                          mostRecentDate: getMostRecentActionDateExcludingEffective(b),
                        };
                        return (
                          <BillCard
                            key={row.bill_id}
                            bill={enriched}
                            isDarkMode={isDarkMode}
                            onBillClick={(bill) => navigate(`/bills/${bill.bill_number}`, { state: { returnUrl: '/account' } })}
                            formatDate={formatDate}
                            getPolicyTagColor={(tag) => getPolicyTagColor(tag, isDarkMode)}
                            getDaysActive={getDaysActive}
                            isFavorited={favoritedIdSet.has(row.bill_id)}
                            isSaved={savedIdSet.has(row.bill_id)}
                            onToggleFavorite={async (bill) => {
                              try {
                                const id = bill.id || (await resolveBillId(bill));
                                if (!id) return;
                                await toggleFavoriteBill(id, false);
                                setFavoritedBills((prev) => prev.filter((r) => r.bill_id !== id));
                              } catch (e) {
                                alert('Failed to update favorite state.');
                              }
                            }}
                            onToggleSave={async (bill) => {
                              try {
                                const id = bill.id || (await resolveBillId(bill));
                                if (!id) return;
                                const shouldSave = !savedIdSet.has(id);
                                await toggleSaveBill(id, shouldSave);
                                setSavedBills((prev) =>
                                  shouldSave
                                    ? [{ bill_id: id, bills: bill }, ...prev.filter((r) => r.bill_id !== id)]
                                    : prev.filter((r) => r.bill_id !== id)
                                );
                              } catch (e) {
                                alert('Failed to update saved state.');
                              }
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
